import { useEffect, useMemo, useState } from 'react'
import { KeyRound, Pencil, Plus, PowerOff, ShieldCheck, SlidersHorizontal } from 'lucide-react'
import Button from '@/components/button'
import ChipSelect from '@/components/chip-select'
import ConfirmDialog from '@/components/confirm-dialog'
import DataTable from '@/components/data-table'
import type { Column } from '@/components/data-table'
import FilterBar from '@/components/filter-bar'
import PageHeader from '@/components/page-header'
import StatusBadge from '@/components/status-badge'
import {
  useAtualizarOperadorMutation,
  useAtualizarSituacaoOperadorMutation,
  useCriarOperadorMutation,
  useResetarSenhaOperadorMutation,
} from '@/http/mutations/operadores'
import { useOperadoresQuery } from '@/http/queries/operadores'
import { SITUACAO_USUARIO } from '@/types/domain'
import type { OperadorResumo } from '@/types/operador'
import { STATUS_OPERADOR, situacaoOperadorBadges } from '@/types/operador'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import { formatTelefone, orDash } from '@/utility/Utils'
import { contarFiltrosDoPainel, filtroVazio, montarChips, paraFiltroApi } from './filtro'
import type { FiltroOperadores } from './filtro'
import OperadorFormModal from './form'
import type { OperadorFormValues } from './form'
import OperadorPermissoesModal from './permissoes'
import styles from './style.module.css'

const operadorEstaAtivo = (operador: OperadorResumo): boolean =>
  operador.situacao === SITUACAO_USUARIO.Ativo

const OperadoresList = () => {
  const { can } = useAuth()
  const podeGerenciar = can(ACAO.GerenciarOperadores)
  const podeGerenciarPermissoes = can(ACAO.GerenciarPermissoes)

  const [busca, setBusca] = useState('')
  const [aplicado, setAplicado] = useState<FiltroOperadores>(filtroVazio)
  const [rascunho, setRascunho] = useState<FiltroOperadores>(filtroVazio)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const [operadorEmEdicao, setOperadorEmEdicao] = useState<OperadorResumo | null>(null)
  const [modalAberto, setModalAberto] = useState(false)
  const [alvoSituacao, setAlvoSituacao] = useState<OperadorResumo | null>(null)
  const [alvoSenha, setAlvoSenha] = useState<OperadorResumo | null>(null)
  const [alvoPermissoes, setAlvoPermissoes] = useState<OperadorResumo | null>(null)

  const buscaDebounced = useDebouncedValue(busca)

  useEffect(() => {
    setAplicado((atual) => (atual.busca === buscaDebounced ? atual : { ...atual, busca: buscaDebounced }))
    setPageNumber(1)
  }, [buscaDebounced])

  const query = useMemo(
    () => ({ ...paraFiltroApi(aplicado, pageSize), pageNumber }),
    [aplicado, pageNumber, pageSize],
  )

  const { data: page, error, isFetching, refetch } = useOperadoresQuery(query)
  const criarOperador = useCriarOperadorMutation(() => setModalAberto(false))
  const atualizarOperador = useAtualizarOperadorMutation(() => setModalAberto(false))
  const atualizarSituacao = useAtualizarSituacaoOperadorMutation()
  const resetarSenha = useResetarSenhaOperadorMutation()

  const efetivar = (proximo: FiltroOperadores) => {
    setAplicado(proximo)
    setRascunho(proximo)
    setBusca(proximo.busca)
    setPageNumber(1)
  }

  const alternarSituacao = (valores: string[]) => {
    const nova = valores.at(-1) as FiltroOperadores['situacao'] | undefined
    efetivar({ ...aplicado, situacao: nova ?? STATUS_OPERADOR.Todas })
  }

  const abrirCriacao = () => {
    setOperadorEmEdicao(null)
    setModalAberto(true)
  }

  const abrirEdicao = (operador: OperadorResumo) => {
    setOperadorEmEdicao(operador)
    setModalAberto(true)
  }

  const salvar = (valores: OperadorFormValues) => {
    if (operadorEmEdicao) {
      if (!operadorEmEdicao.id) return

      atualizarOperador.mutate({
        id: operadorEmEdicao.id,
        nome: valores.nome,
        email: valores.email,
        telefone: valores.telefone,
        telefoneDDI: valores.telefoneDDI,
      })
      return
    }

    criarOperador.mutate({
      nome: valores.nome,
      email: valores.email,
      telefone: valores.telefone,
      telefoneDDI: valores.telefoneDDI,
    })
  }

  const colunaAcoes: Column<OperadorResumo> = {
    key: 'acoes',
    header: '',
    align: 'right',
    width: '240px',
    render: (operador) => (
      <div className={styles.acoes}>
        {podeGerenciar && (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Editar operador"
            title="Editar"
            onClick={() => abrirEdicao(operador)}
          >
            <Pencil size={16} />
          </Button>
        )}
        {podeGerenciarPermissoes && (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Permissões do operador"
            title="Permissões"
            onClick={() => setAlvoPermissoes(operador)}
          >
            <SlidersHorizontal size={16} />
          </Button>
        )}
        {podeGerenciar && (
          <>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Redefinir senha"
              title="Redefinir senha"
              onClick={() => setAlvoSenha(operador)}
            >
              <KeyRound size={16} />
            </Button>
            <Button
              variant={operadorEstaAtivo(operador) ? 'danger' : 'success'}
              size="sm"
              iconLeft={
                operadorEstaAtivo(operador) ? <PowerOff size={14} /> : <ShieldCheck size={14} />
              }
              onClick={() => setAlvoSituacao(operador)}
            >
              {operadorEstaAtivo(operador) ? 'Inativar' : 'Reativar'}
            </Button>
          </>
        )}
      </div>
    ),
  }

  const columns: Column<OperadorResumo>[] = [
    {
      key: 'nome',
      header: 'Nome',
      render: (operador) => (
        <div className={styles.stack}>
          <strong>{orDash(operador.nome)}</strong>
          <span className={styles.muted}>{orDash(operador.email)}</span>
        </div>
      ),
    },
    {
      key: 'telefone',
      header: 'Telefone',
      render: (operador) => formatTelefone(operador.telefone1, operador.telefone1DDI),
    },
    {
      key: 'situacao',
      header: 'Situação',
      render: (operador) => (
        <StatusBadge
          label={operadorEstaAtivo(operador) ? 'Ativo' : 'Inativo'}
          tone={operadorEstaAtivo(operador) ? 'success' : 'neutral'}
        />
      ),
    },
    ...(podeGerenciar || podeGerenciarPermissoes ? [colunaAcoes] : []),
  ]

  return (
    <>
      <PageHeader
        title="Operadores"
        subtitle="Cadastro e permissões dos operadores que atendem no Admin IntegraDoor"
        actions={
          podeGerenciar ? (
            <Button iconLeft={<Plus size={16} />} onClick={abrirCriacao}>
              Novo operador
            </Button>
          ) : null
        }
      />

      <FilterBar
        searchValue={busca}
        onSearchChange={setBusca}
        onSearchClear={() => efetivar({ ...aplicado, busca: '' })}
        searchPlaceholder="Buscar por nome ou email"
        searchLabel="Buscar operadores"
        isSearching={isFetching && busca !== buscaDebounced}
        chips={montarChips(aplicado, (campo) => efetivar({ ...aplicado, [campo]: filtroVazio[campo] }))}
        activeCount={contarFiltrosDoPainel(aplicado)}
        onApply={() => efetivar({ ...rascunho, busca: aplicado.busca })}
        onClear={() => efetivar(filtroVazio)}
        advanced={null}
        quickFilters={
          <ChipSelect
            options={situacaoOperadorBadges}
            value={rascunho.situacao === STATUS_OPERADOR.Todas ? [] : [rascunho.situacao]}
            onChange={alternarSituacao}
            ariaLabel="Situação do operador"
          />
        }
      />

      <DataTable
        columns={columns}
        page={page}
        getRowKey={(operador) => operador.id ?? ''}
        isLoading={isFetching}
        error={error}
        onRetry={() => void refetch()}
        onPageChange={setPageNumber}
        pageSize={pageSize}
        onPageSizeChange={(novoTamanho) => {
          setPageSize(novoTamanho)
          setPageNumber(1)
        }}
        emptyTitle="Nenhum operador encontrado"
        emptyDescription="Ajuste os filtros e tente novamente."
      />

      <OperadorFormModal
        isOpen={modalAberto}
        toggle={() => setModalAberto(false)}
        operador={operadorEmEdicao}
        isLoading={criarOperador.isPending || atualizarOperador.isPending}
        onSubmit={salvar}
      />

      <OperadorPermissoesModal
        isOpen={Boolean(alvoPermissoes)}
        toggle={() => setAlvoPermissoes(null)}
        operador={alvoPermissoes}
      />

      <ConfirmDialog
        isOpen={Boolean(alvoSituacao)}
        toggle={() => setAlvoSituacao(null)}
        title={alvoSituacao && operadorEstaAtivo(alvoSituacao) ? 'Inativar operador' : 'Reativar operador'}
        message={
          alvoSituacao && operadorEstaAtivo(alvoSituacao)
            ? `O acesso de ${orDash(alvoSituacao?.nome)} ao backoffice será negado a partir do próximo login. O histórico de ações que ele já executou continua preservado.`
            : `${orDash(alvoSituacao?.nome)} voltará a ter acesso ao backoffice.`
        }
        confirmLabel={alvoSituacao && operadorEstaAtivo(alvoSituacao) ? 'Inativar operador' : 'Reativar operador'}
        tone={alvoSituacao && operadorEstaAtivo(alvoSituacao) ? 'danger' : 'success'}
        isLoading={atualizarSituacao.isPending}
        onConfirm={() => {
          if (!alvoSituacao?.id) return

          atualizarSituacao.mutate(
            { operadorId: alvoSituacao.id, ativar: !operadorEstaAtivo(alvoSituacao) },
            { onSuccess: () => setAlvoSituacao(null) },
          )
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(alvoSenha)}
        toggle={() => setAlvoSenha(null)}
        title="Redefinir senha"
        message={`A senha de ${orDash(alvoSenha?.nome)} volta para a senha padrão. Ele vai precisar trocá-la no próximo acesso.`}
        confirmLabel="Redefinir senha"
        tone="danger"
        isLoading={resetarSenha.isPending}
        onConfirm={() => {
          if (!alvoSenha?.id) return

          resetarSenha.mutate(alvoSenha.id, { onSuccess: () => setAlvoSenha(null) })
        }}
      />
    </>
  )
}

export default OperadoresList
